import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";

import APAccordion from "../../../components/APAccordion";
import AIButton from "../../../components/buttons/APOutlineButtons/AIButton";
import { ExpandCollapseAllButton } from "../../../components/buttons/ExpandCollapseAllButton";
import InfoButton from "../../../components/buttons/InfoButton";
import VehicleImageCarousel from "../../../components/carousels/VehicleImageCarousel";
import { useExpandCollapseAll } from "../../../hooks/useExpandCollapseAll";
import { FaqProps } from "../../../types/Faq";
import {
  APVehicleVariant,
  RedbookVehiclePhoto,
  VehicleOptions,
} from "../../../types/Vehicles";
import { VehicleVariantSummarisedSpecs } from "../../vehicleSearch/components/VehicleVariantSummarisedSpecs";
import APVehicleDetailTabs, { VehicleDetailTab } from "./VehicleDetailTabs";
import VehicleOptionsList, {
  VehicleOptionListVariant,
} from "./VehicleOptionsList";
import VehicleStandardFeatures from "./VehicleStandardFeatures";

export type DisplaySections = {
  images: boolean;
  specs: boolean;
  details: boolean;
  options: boolean;
  manufacturerOptions: boolean;
  standardFeatures: boolean;
  aiReview: boolean;
  aiSuggestions: boolean;
};

export type onEditActions = {
  onEditVehicle?: () => void;
  onEditOptions?: () => void;
  onEditManufacturerOptions?: () => void;
};

export type ExpandedSections = {
  details?: boolean;
  options?: boolean;
  manufacturerOptions?: boolean;
  standardFeatures?: boolean;
};

export type VehicleDetailSlots = {
  belowSpecs?: React.ReactNode;
  besideSpecs?: React.ReactNode;
  belowDetails?: React.ReactNode;
};

type VehicleOptionListProps = {
  variant?: VehicleOptionListVariant;
  aboveOptionsContent?: React.ReactNode;
  filterable?: boolean;
  emptyMessage?: string;
};

type OptionsProps = {
  options: VehicleOptionListProps;
  manufacturerOptions: VehicleOptionListProps;
};

type VehicleDetailProps = {
  vehicleVariant: APVehicleVariant;
  vehicleImages: RedbookVehiclePhoto[];
  vehicleOptions: VehicleOptions | null;
  shouldPromptForAIConsent: boolean;
  onAISuggestions: () => void;
  onAIReview: () => void;
  faqProps?: FaqProps;
  priceToBeConfirmed?: boolean;
  displaySections: DisplaySections;
  hideExpandCollapseAllButton?: boolean;
  slots?: VehicleDetailSlots;
  editActions?: onEditActions;
  expandedSections?: ExpandedSections;
  optionsProps?: OptionsProps;
};

const vehicleDetailTabs: VehicleDetailTab[] = [
  {
    label: "Vehicle",
    fields: [
      "listPriceIncGst",
      "class",
      "segment",
      "bodyType",
      "seatCapacity",
      "doors",
      "co2Combined",
      "ancaprating",
      "warrantyYears",
      "regularServiceIntervalMonths",
      "regularServiceIntervalKms",
      "countryOfOrigin",
    ],
  },
  {
    label: "Power Plant",
    fields: [
      "powerKw",
      "torque",
      "powerToWeight",
      "powerPlantType",
      "acceleration",
      "range",
      "batteryType",
      "driveDescription",
      "transmission",
      "fuelType",
      "fuelEconomy",
    ],
  },
  {
    label: "Payload",
    fields: [
      "minCargoVolume",
      "maxCargoVolume",
      "towingUnbrakedKg",
      "towingBrakedKg",
      "payload",
    ],
  },
  { label: "Wheels", fields: ["rimMaterial", "frontRim", "rearRim"] },
  { label: "Dimensions", fields: ["length", "width", "height", "kerbWeight"] },
];

const APVehicleDetail = ({
  vehicleVariant,
  vehicleImages,
  vehicleOptions,
  shouldPromptForAIConsent,
  onAISuggestions,
  onAIReview,
  faqProps,
  priceToBeConfirmed = false,
  displaySections,
  hideExpandCollapseAllButton = false,
  slots,
  editActions,
  expandedSections = {
    details: true,
    options: false,
    manufacturerOptions: false,
    standardFeatures: false,
  },
  optionsProps,
}: VehicleDetailProps) => {
  const expandCollapseAll = useExpandCollapseAll();

  return (
    <>
      <Stack spacing={2}>
        {displaySections.images && (
          <Box>
            <APAccordion
              title={`${vehicleVariant.year} ${vehicleVariant.make} ${vehicleVariant.model} ${vehicleVariant.variant}`}
              hideExpand={true}
              alwaysExpanded={true}
              leftIcon={
                editActions?.onEditVehicle ? <EditOutlinedIcon /> : undefined
              }
              handleLeftIconClick={editActions?.onEditVehicle}
              smallHeader={true}
            >
              {vehicleImages.length > 0 && (
                <Box pt={2}>
                  <VehicleImageCarousel images={vehicleImages} />
                </Box>
              )}
            </APAccordion>
          </Box>
        )}

        {displaySections.specs && (
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <VehicleVariantSummarisedSpecs
              vehicleVariant={vehicleVariant}
              includeRows={["money", "car", "speedometer", "range"]}
              priceToBeConfirmed={priceToBeConfirmed}
            />
            {slots?.besideSpecs && <Box>{slots.besideSpecs}</Box>}

            {!slots?.besideSpecs && (
              <Box mr={1}>
                {faqProps &&
                  faqProps.renderAppLocation(
                    "VehicleSpec-Page",
                    <InfoButton />,
                    true
                  )}
              </Box>
            )}
          </Box>
        )}

        {slots?.belowSpecs && <Box>{slots.belowSpecs}</Box>}

        {displaySections.aiReview && (
          <Box>
            <AIButton
              type="vehicle-review"
              onClick={() => onAIReview()}
              shouldPromptForAIConsent={shouldPromptForAIConsent}
              faqProps={faqProps}
              faqLocationIdentifier="VehicleSpec-AIVehicleReview"
            />
          </Box>
        )}

        {displaySections.details && (
          <Box>
            {!hideExpandCollapseAllButton && (
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <ExpandCollapseAllButton controller={expandCollapseAll} />
              </Box>
            )}
            <APVehicleDetailTabs
              variant={vehicleVariant}
              vehicleDetailTabs={vehicleDetailTabs}
              defaultExpanded={expandedSections.details}
              forceOpen={expandCollapseAll.isAllExpanded}
              forceClose={expandCollapseAll.isAllCollapsed}
              priceToBeConfirmed={priceToBeConfirmed}
            />
          </Box>
        )}

        {slots?.belowDetails && <Box>{slots.belowDetails}</Box>}

        {displaySections.standardFeatures && vehicleOptions && (
          <Box>
            <VehicleStandardFeatures
              features={vehicleOptions.standardEquipment}
              filterable={true}
              forceOpen={expandCollapseAll.isAllExpanded}
              forceClose={expandCollapseAll.isAllCollapsed}
              defaultExpanded={expandedSections.standardFeatures}
            />
          </Box>
        )}

        {displaySections.options && vehicleOptions && (
          <Box>
            <VehicleOptionsList
              title="Options"
              aboveOptionsContent={optionsProps?.options.aboveOptionsContent}
              options={[
                ...Object.values(vehicleOptions.options.dealer),
                ...Object.values(vehicleOptions.options.inclusion),
              ]}
              filterable={optionsProps?.options.filterable ?? true}
              forceOpen={expandCollapseAll.isAllExpanded}
              forceClose={expandCollapseAll.isAllCollapsed}
              defaultExpanded={expandedSections.options}
              editAction={editActions?.onEditOptions}
              variant={optionsProps?.options.variant}
              emptyMessage={optionsProps?.options.emptyMessage}
            />
          </Box>
        )}

        {displaySections.manufacturerOptions && vehicleOptions && (
          <Box>
            <VehicleOptionsList
              title="Manufacturer Options"
              aboveOptionsContent={
                optionsProps?.manufacturerOptions.aboveOptionsContent
              }
              options={Object.values(vehicleOptions.options.manufacturer)}
              filterable={optionsProps?.manufacturerOptions.filterable ?? true}
              forceOpen={expandCollapseAll.isAllExpanded}
              forceClose={expandCollapseAll.isAllCollapsed}
              defaultExpanded={expandedSections.manufacturerOptions}
              editAction={editActions?.onEditManufacturerOptions}
              variant={optionsProps?.manufacturerOptions.variant}
              emptyMessage={optionsProps?.manufacturerOptions.emptyMessage}
            />
          </Box>
        )}

        {displaySections.aiSuggestions && (
          <Box>
            <AIButton
              type="vehicle-suggestions"
              onClick={() => onAISuggestions()}
              shouldPromptForAIConsent={shouldPromptForAIConsent}
              faqProps={faqProps}
              faqLocationIdentifier="VehicleSpec-AIVehicleSuggestions"
            />
          </Box>
        )}
      </Stack>
    </>
  );
};

export default APVehicleDetail;
